import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAuditCatalogue } from '../../context/AuditCatalogueContext';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { adminUsersApi } from '../../services/adminUsersApi';
import type { AdminActionResponse } from '../../types';
import CursorPagination from '../CursorPagination';
import ActionSelect from './ActionSelect';
import { formatGst, humanizeEntityType, visibleEntityRoute } from './auditFormat';
import { validateRange } from './AuditFilters';

interface AdminActionsCardProps {
  /** users-service id of the admin (the endpoint's path variable). */
  userId: string;
  /** Keycloak subject of the admin (the Audit screen's admin filter). */
  subjectId: string;
}

const PAGE_SIZE = 20;

/**
 * Spec 069 Flow C — the admin-user "Admin Actions" tab, read from the unified audit. The action list
 * is the catalogue (AC-C4), dates are Dubai days sent as `YYYY-MM-DD` (AC-C3), a reversed range is an
 * inline error with no request, failures are shown (never swallowed) and a slower older response can
 * never overwrite a newer filter's rows.
 */
export default function AdminActionsCard({ userId, subjectId }: AdminActionsCardProps) {
  const { token, isSuperAdmin, hasScreen } = useAuth();
  const features = useFeatureFlags();
  const { labelOf } = useAuditCatalogue();
  const uid = useId();

  const [actions, setActions] = useState<string[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState<AdminActionResponse[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestToken = useRef(0);

  const rangeError = validateRange(from || undefined, to || undefined);
  const filtered = actions.length > 0 || !!from || !!to;

  const load = useCallback(async (cursor?: string) => {
    if (!token) return;
    const mine = ++requestToken.current;
    if (cursor) setLoadingMore(true); else { setLoading(true); setError(null); }
    try {
      const page = await adminUsersApi.getAdminActions(userId, {
        actionType: actions.length ? actions.join(',') : undefined,
        dateFrom: from || undefined,
        dateTo: to || undefined,
        cursor,
        size: PAGE_SIZE,
      }, token);
      if (mine !== requestToken.current) return;
      setRows((prev) => (cursor ? [...prev, ...page.content] : page.content));
      setNextCursor(page.nextCursor || undefined);
      setHasMore(page.hasMore);
    } catch (err) {
      if (mine !== requestToken.current) return;
      setError(err instanceof Error ? err.message : 'Could not load admin actions');
      if (!cursor) { setRows([]); setHasMore(false); }
    } finally {
      if (mine === requestToken.current) { setLoading(false); setLoadingMore(false); }
    }
  }, [token, userId, actions, from, to]);

  useEffect(() => {
    if (rangeError) {
      requestToken.current++; // nothing is sent, and anything in flight is discarded
      setLoading(false);
      return;
    }
    const t = window.setTimeout(() => load(), 300);
    return () => window.clearTimeout(t);
  }, [load, rangeError]);

  const auditHref = (() => {
    const p = new URLSearchParams();
    p.set('admin', subjectId);
    actions.forEach((a) => p.append('action', a));
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return `/audit?${p.toString()}`;
  })();
  const canOpenAudit = features.auditScreen && hasScreen('audit') && !!subjectId;

  return (
    <div className="admin-card" style={{ marginBottom: '1.25rem' }} data-testid="admin-actions-card">
      <div className="audit-card-head">
        <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Admin Actions</h3>
        {canOpenAudit && <Link to={auditHref} className="admin-btn admin-btn-outline admin-btn-sm">Open in Audit</Link>}
      </div>
      <div className="audit-filters audit-filters--compact">
        <ActionSelect label="Action" value={actions} onChange={setActions} />
        <div className="audit-field">
          <label htmlFor={`${uid}-from`} className="audit-field__label">From (GST)</label>
          <input id={`${uid}-from`} type="date" className="admin-form-input" value={from} onChange={(e) => setFrom(e.target.value)}
            aria-invalid={!!rangeError} aria-describedby={rangeError ? `${uid}-err` : undefined} />
        </div>
        <div className="audit-field">
          <label htmlFor={`${uid}-to`} className="audit-field__label">To (GST)</label>
          <input id={`${uid}-to`} type="date" className="admin-form-input" value={to} onChange={(e) => setTo(e.target.value)}
            aria-invalid={!!rangeError} aria-describedby={rangeError ? `${uid}-err` : undefined} />
        </div>
        {rangeError && <p id={`${uid}-err`} className="audit-hint audit-hint--error audit-filters__error" role="alert">{rangeError}</p>}
      </div>

      {error && (
        <div className="admin-info-banner danger audit-error" role="alert">
          Could not load admin actions: {error}{' '}
          <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => load()}>Retry</button>
        </div>
      )}

      {rangeError ? null : loading ? (
        <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
      ) : error ? null : rows.length === 0 ? (
        <div className="admin-empty-state"><p>{filtered ? 'No actions match.' : 'No admin actions recorded.'}</p></div>
      ) : (
        <>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead><tr><th scope="col">Action</th><th scope="col">Entity</th><th scope="col">Garage</th><th scope="col">When (GST)</th><th scope="col"></th></tr></thead>
              <tbody>
                {rows.map((a) => {
                  const route = visibleEntityRoute(
                    { entityType: a.entityType, entityId: a.entityId, entityName: a.entityName, garageId: a.garageId, details: a.details },
                    isSuperAdmin, hasScreen,
                  );
                  return (
                    <tr key={a.id}>
                      <td><span className="admin-badge pending">{a.actionLabel || labelOf(a.actionType)}</span></td>
                      <td>
                        <span className="audit-entity-type">{humanizeEntityType(a.entityType)}</span>{' '}
                        <span style={{ fontWeight: 500 }}>{a.entityName || '—'}</span>
                      </td>
                      <td>{a.garageName || '—'}</td>
                      <td>{formatGst(a.performedAt)}</td>
                      <td>{route ? <Link to={route} className="admin-btn admin-btn-outline admin-btn-sm">View</Link> : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <CursorPagination hasMore={hasMore} loading={loadingMore} onLoadMore={() => load(nextCursor)} />
        </>
      )}
    </div>
  );
}
